import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { X, BookOpen, Users, Video, Megaphone, Sparkles, Star } from 'lucide-react';

const ICONS = {
  school: Users,
  book: BookOpen,
  videocam: Video,
  megaphone: Megaphone,
  sparkles: Sparkles,
  star: Star,
};

const SAMPLE_LOCATIONS = ['Dar es Salaam', 'Arusha', 'Mwanza', 'Dodoma', 'Mbeya', 'Tanga', 'Zanzibar', 'Morogoro'];

export default function SocialProofPopup() {
  const [visible, setVisible] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const indexRef = useRef(0);
  const timerRef = useRef(null);

  // Fetch settings and events
  useEffect(() => {
    const load = async () => {
      try {
        const [settingsRes, eventsRes, manualRes] = await Promise.all([
          axios.get(`${API}/campaigns/popup-settings`).catch(() => ({ data: null })),
          axios.get(`${API}/campaigns/latest-events?limit=30`).catch(() => ({ data: [] })),
          axios.get(`${API}/campaigns/manual-popups`).catch(() => ({ data: [] })),
        ]);

        const s = settingsRes.data || { enabled: true, frequency_seconds: 45 };
        setSettings(s);

        // Merge manual popups (higher priority) with auto events
        const manuals = (manualRes.data || []).map(p => ({
          ...p, event_type: 'custom', message: p.message, icon: p.icon || 'megaphone'
        }));
        const autos = eventsRes.data || [];
        
        // If no events exist, generate sample ones
        let allEvents = [...manuals, ...autos];
        if (allEvents.length === 0) {
          allEvents = generateSampleEvents();
        }
        setEvents(allEvents);
      } catch {}
    };
    load();
    const refresh = setInterval(load, 120000); // Refresh every 2 min
    return () => clearInterval(refresh);
  }, []);

  const generateSampleEvents = () => {
    const samples = [
      { event_type: 'enrollment', message: `A student in ${randomLoc()} just enrolled in a Mathematics course`, icon: 'school' },
      { event_type: 'purchase', message: `Someone in ${randomLoc()} just got a Physics ebook`, icon: 'book' },
      { event_type: 'live_class_booking', message: `A student in ${randomLoc()} booked a seat for Chemistry Live Class`, icon: 'videocam' },
      { event_type: 'enrollment', message: `A student in ${randomLoc()} just enrolled in English Grammar`, icon: 'school' },
      { event_type: 'purchase', message: `Someone in ${randomLoc()} purchased a Biology textbook`, icon: 'book' },
    ];
    return samples;
  };

  const randomLoc = () => SAMPLE_LOCATIONS[Math.floor(Math.random() * SAMPLE_LOCATIONS.length)];

  // Rotation timer
  useEffect(() => {
    if (!settings?.enabled || events.length === 0 || dismissed) return;

    const freq = (settings.frequency_seconds || 45) * 1000;

    // Show first after 10s delay
    const initialDelay = setTimeout(() => {
      showNext();
    }, 10000);

    timerRef.current = setInterval(() => {
      showNext();
    }, freq);

    return () => {
      clearTimeout(initialDelay);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [settings, events, dismissed]);

  const showNext = useCallback(() => {
    if (events.length === 0) return;
    const event = events[indexRef.current % events.length];
    indexRef.current++;
    setCurrentEvent(event);
    setVisible(true);

    // Auto-hide after 6s
    setTimeout(() => setVisible(false), 6000);
  }, [events]);

  if (!settings?.enabled || !currentEvent || dismissed) return null;

  const IconComp = ICONS[currentEvent.icon] || Sparkles;
  const typeColors = {
    enrollment: 'from-blue-500 to-indigo-600',
    purchase: 'from-emerald-500 to-teal-600',
    live_class_booking: 'from-red-500 to-rose-600',
    custom: 'from-amber-500 to-orange-600',
  };
  const bgGradient = typeColors[currentEvent.event_type] || typeColors.custom;

  return (
    <div
      className={`fixed bottom-6 right-6 z-[9999] transition-all duration-500 ease-out ${
        visible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95 pointer-events-none'
      }`}
      data-testid="social-proof-popup"
    >
      <div className="bg-white rounded-xl shadow-2xl shadow-black/15 border border-slate-200 max-w-xs overflow-hidden">
        <div className="flex items-start gap-3 p-3.5">
          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${bgGradient} flex items-center justify-center shrink-0`}>
            <IconComp className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-800 font-medium leading-snug">{currentEvent.message}</p>
            <p className="text-[10px] text-slate-400 mt-1">Just now</p>
          </div>
          <button
            onClick={() => { setVisible(false); setDismissed(true); }}
            className="text-slate-300 hover:text-slate-500 transition-colors shrink-0 mt-0.5"
            data-testid="dismiss-social-proof"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {currentEvent.link && (
          <a href={getSectionLink(currentEvent)} className="block text-xs text-center py-1.5 bg-slate-50 text-indigo-600 font-semibold hover:bg-slate-100 transition-colors border-t border-slate-100">
            Get Yours Now &rarr;
          </a>
        )}
      </div>
    </div>
  );
}

function getSectionLink(event) {
  const type = event.event_type || '';
  if (type === 'enrollment' || event.link?.includes('/courses/')) return '/all-courses';
  if (type === 'purchase' || event.link?.includes('/ebooks/')) return '/ebooks';
  if (type === 'live_class_booking' || event.link?.includes('/live-classes/')) return '/live-classes';
  if (event.link?.includes('/exams/')) return '/exams';
  if (event.link?.includes('/notes/')) return '/notes';
  return event.link || '/';
}
